import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("login.success", { defaultValue: "Signed in" }));
      nav("/");
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-10 text-center">
          <div className="font-display text-5xl leading-none text-stone-900">progres.</div>
          <div className="text-sm text-stone-500 mt-3">{t("login.tagline", { defaultValue: "Your progress. Month by month." })}</div>
        </div>

        <div className="card-soft p-8">
          <h1 className="font-display text-3xl text-stone-900 mb-2 leading-tight">
            {t("login.welcomeBack", { defaultValue: "Welcome back." })}
          </h1>
          <p className="text-sm text-stone-500 mb-8">{t("login.signInToContinue", { defaultValue: "Sign in to continue." })}</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">{t("auth.email")}</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 text-stone-900 rounded-2xl px-4 py-3 text-sm focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-stone-700">{t("auth.password")}</label>
                <Link
                  to="/forgot-password"
                  data-testid="link-forgot-password"
                  className="text-[11px] text-stone-500 hover:text-stone-900 font-semibold"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 text-stone-900 rounded-2xl px-4 py-3 text-sm focus:outline-none"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3"
              >
                {err}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="btn-pill w-full"
            >
              {busy ? t("login.busy", { defaultValue: "Signing in…" }) : (<>{t("auth.loginBtn")} <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-stone-100 text-center text-sm text-stone-500">
            {t("auth.noAccount")}{" "}
            <Link
              data-testid="link-register"
              to="/register"
              className="text-stone-900 font-semibold hover:underline underline-offset-4 ml-1"
            >
              {t("auth.registerBtn")}
            </Link>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] text-stone-600 font-medium">
          <Link to="/regulamin" className="hover:text-stone-700">{t("nav.regulations")}</Link>
          <Link to="/polityka-prywatnosci" className="hover:text-stone-700">{t("nav.privacy")}</Link>
          <Link to="/umowa-powierzenia" className="hover:text-stone-700">{t("nav.dpa")}</Link>
        </div>
      </div>
    </main>
  );
}
